/**
 * Canonical 5D ability scores for reports, dashboards, and LLM prompts.
 *
 * **Do not bypass this module** for ability display. The backend stores scores in
 * `user.ability[5]`. The deprecated `user.ability_6d[6]` mirror duplicates critical
 * thinking at index 4 and maps communication to index 5 — reading it with legacy labels
 * (Memory, Creativity, Graphical Reasoning) produces wrong UI.
 *
 * Slugs and order: `CORE_ABILITY_SLUGS` in `../data/abilityPapers.js`
 * Backend indices: `config/productVectors.js` (`ABILITY_PAPER_TO_INDEX`)
 * Docs: `client/src/data/README.md` § Ability scores in UI; `BACKEND_STRUCTURE.md` §3.1
 *
 * @module abilityVectorDisplay
 */
import { CORE_ABILITY_SLUGS } from '../data/abilityPapers';

const DEFAULT_ABILITY_VECTOR = [1.5, 1.5, 1.5, 1.5, 1.5];

/** Maximum value for a single ability dimension in UI and downstream reads. */
export const MAX_ABILITY_SCORE = 5;

/**
 * Manual filter: cap ability scores that exceed the product maximum.
 * @param {number} value
 * @returns {number}
 */
export function clampAbilityScore(value) {
  if (!Number.isFinite(value)) return value;
  return Math.min(MAX_ABILITY_SCORE, value);
}

/** i18n keys under `report.*` for each core ability slug. */
export const ABILITY_REPORT_I18N_KEYS = {
  'quantitative-reasoning': 'report.quantitativeReasoning',
  'logical-reasoning': 'report.logicalReasoning',
  'critical-thinking': 'report.criticalThinking',
  'verbal-reasoning': 'report.verbalReasoning',
  communication: 'report.communication'
};

/** English labels for LLM / prompt context. */
export const ABILITY_EN_LABELS = {
  'quantitative-reasoning': 'Quantitative Reasoning',
  'logical-reasoning': 'Logical Reasoning',
  'critical-thinking': 'Critical Thinking',
  'verbal-reasoning': 'Verbal Reasoning',
  communication: 'Communication'
};

function scoreAt(raw, index) {
  const n = Number(raw?.[index]);
  const value = Number.isFinite(n) ? n : DEFAULT_ABILITY_VECTOR[index];
  return clampAbilityScore(value);
}

/**
 * Canonical 5D ability vector for display. Prefers `user.ability`; falls back to
 * unpacking the legacy `ability_6d` mirror (communication lives at index 5).
 * Scores above {@link MAX_ABILITY_SCORE} are capped via {@link clampAbilityScore}.
 * @param {{ ability?: number[], ability_6d?: number[] } | null | undefined} userData
 * @returns {number[]}
 */
export function normalizeUserAbilityVector(userData) {
  if (Array.isArray(userData?.ability) && userData.ability.length >= 5) {
    return CORE_ABILITY_SLUGS.map((_, i) => scoreAt(userData.ability, i));
  }

  const legacy = userData?.ability_6d;
  if (Array.isArray(legacy) && legacy.length >= 6) {
    return [
      scoreAt(legacy, 0),
      scoreAt(legacy, 1),
      scoreAt(legacy, 2),
      scoreAt(legacy, 3),
      scoreAt(legacy, 5)
    ];
  }

  return [...DEFAULT_ABILITY_VECTOR];
}

/**
 * @param {{ ability?: number[], ability_6d?: number[] } | null | undefined} userData
 * @returns {{ slug: string, value: number, i18nKey: string }[]}
 */
export function getAbilityScoresForDisplay(userData) {
  const values = normalizeUserAbilityVector(userData);
  return CORE_ABILITY_SLUGS.map((slug, i) => ({
    slug,
    value: values[i],
    i18nKey: ABILITY_REPORT_I18N_KEYS[slug]
  }));
}
